import { useMemo, useState } from 'react';
import { useAppContext } from '../components/Layout';
import Spinner from '../components/Spinner';
import { useLoginProfiles } from '../hooks/useUserAccess';
import { useDirectory } from '../hooks/useScopedData';
import { canManageRoster, isAdmin, isKeyThree } from '../lib/visibility';
import {
  deriveAccess,
  findPositions,
  recName,
  type AppSettings,
  type Contact,
  type DirectoryPerson,
  type UserProfile,
} from '../types';
import Modal from '../components/Modal';
import Drawer from '../components/Drawer';
import { searchDirectory } from '../lib/directorySearch';
import RefBadge from '../components/RefBadge';
import { deleteContact, grantLogin, mutateDistrictAffiliation, removeLogin, updateContact, updatePersonContactInfo } from '../lib/db';
import DistrictAffiliationsSection from '../components/DistrictAffiliationsSection';
import PositionList from '../components/PositionList';
import { displayedPositions } from '../lib/directoryPositions';
import { canEditRosterRecord, currentDirectoryEntry, rosterEditBasis, rosterSaveOptions } from '../lib/contactAffiliations';
import { usePersonEntry } from '../components/PersonEntry';
import ManualEntryNotice from '../components/ManualEntryNotice';
import ImportMatchReview, { useImportMatches } from '../components/ImportMatchReview';

import { EmailLink, PhoneLink } from '../components/ContactLink';
/** What the "Give this person a login" checkbox on the Contact form should
 *  actually do on save — decided by ContactForm, since only it knows the
 *  viewer's tier and the checked positions' derived access. */
type LoginInstruction =
  | { kind: 'skip' }
  | { kind: 'remove' }
  | { kind: 'grant'; role: 'member' | 'chair'; districtView: boolean; keyThree: boolean; committees: string[] };

type ContactFormValue = Omit<Contact, 'id' | 'tenantId' | 'email' | 'visibility'> & { email: string };

const PAGE_SIZE = 100;

export default function Contacts() {
  const { profile, districtFilter, settings } = useAppContext();
  const { data, loading, error, districtAffiliationsEnabled } = useDirectory(profile);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [open, setOpen] = useState<DirectoryPerson | null>(null);
  const [loginWarning, setLoginWarning] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [onlyPositions, setOnlyPositions] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);
  const { enter: enterPerson, dialog: personEntryDialog } = usePersonEntry();
  const [manualNotice, setManualNotice] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const importMatches = useImportMatches(isAdmin(profile));
  // The drawer shows the current copy of the person, so a change made inside it (an affiliation, say) appears without reopening.
  const current = open ? currentDirectoryEntry(data, open) : null;

  const dName = (id: string) => recName(settings.districts, id);
  const canAdd = settings.districts.some((d) => canManageRoster(profile, d.id));

  // Key 3 (and admins) can see who already has a login, and at what level,
  // so the "Give this person a login" control can reflect reality instead
  // of writing blind — a plain chair has no such read access (see
  // firestore.rules), so this stays empty for them and their side of the
  // form stays a blind grant-only checkbox.
  const canGrantElevated = isAdmin(profile) || isKeyThree(profile);
  const usersQ = useLoginProfiles(profile, canGrantElevated);
  const usersByEmail = useMemo(() => {
    const m = new Map<string, UserProfile>();
    for (const u of usersQ.data) if (u.email) m.set(u.email.toLowerCase(), u);
    return m;
  }, [usersQ.data]);

  const filtered = useMemo(() => {
    const inView = data.filter((p) => {
      if (districtFilter !== 'All' && !p.districtIds.includes(districtFilter)) return false;
      if (onlyPositions && displayedPositions(p).length === 0) return false;
      return true;
    });
    return searchDirectory(inView, query, dName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, districtFilter, onlyPositions, query, settings.districts]);

  const visible = filtered.slice(0, shown);
  const showPrivateColumns = data.some((p) => p.visibility === 'full');
  const source = (s: 'manual' | 'imported' | undefined) =>
    s === 'manual' ? <span className="subtitle" title="Entered by hand — imports never overwrite this"> · manual</span> : null;

  return (
    <div className="card">
      <div className="row">
        <div>
          <h2>Contacts</h2>
          <div className="subtitle">
            Everyone Compass knows about — {loading ? '…' : `${filtered.length.toLocaleString()} ${filtered.length === 1 ? 'person' : 'people'}`}
            {showPrivateColumns ? ', with how to reach them where you have access.' : '.'}
          </div>
        </div>
        {canAdd && (
          <button className="btn primary" onClick={() => setManualNotice(true)}>
            + Add Person Manually
          </button>
        )}
      </div>

      {importMatches.matches.length > 0 && (
        <div className="subtitle" style={{ margin: '10px 0', padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 8 }}>
          {importMatches.matches.length === 1 ? 'One person' : `${importMatches.matches.length} people`} in your my.Scouting.org import may already be in
          Compass as someone added manually. Nothing was created or joined — please confirm.{' '}
          <button className="linkbtn" onClick={() => setReviewOpen(true)}>Review</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        <input
          style={{ flex: '1 1 280px', maxWidth: 420 }}
          type="search"
          placeholder={showPrivateColumns ? 'Search name, position, district, email or phone…' : 'Search name, position or district…'}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShown(PAGE_SIZE); }}
          aria-label="Search contacts"
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={onlyPositions} onChange={(e) => { setOnlyPositions(e.target.checked); setShown(PAGE_SIZE); }} />
          <span className="subtitle">Only people with Org Chart positions</span>
        </label>
      </div>

      {loading && <div className="empty"><Spinner label="Loading…" /></div>}
      {error && <div className="err">{error.message}</div>}
      {loginWarning && <div className="err">{loginWarning}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>District</th>
              <th>Position</th>
              {showPrivateColumns && <th>Email</th>}
              {showPrivateColumns && <th>Phone</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.personId ?? p.contactId ?? p.name} className="uh-row" onClick={() => setOpen(p)} style={{ cursor: 'pointer' }}>
                <td>
                  <b>{p.name || 'Vacant'}</b> <RefBadge value={p.ref} />
                </td>
                <td>{p.districtIds.length ? p.districtIds.map(dName).join(', ') : <span className="subtitle">Council / none</span>}</td>
                <td><PositionList person={p} /></td>
                {showPrivateColumns && <td>{p.visibility === 'full' ? <><EmailLink email={p.email} />{source(p.emailSource)}</> : ''}</td>}
                {showPrivateColumns && <td>{p.visibility === 'full' ? <><PhoneLink phone={p.phone} />{source(p.phoneSource)}</> : ''}</td>}
                <td><button className="btn" onClick={(e) => { e.stopPropagation(); setOpen(p); }}>Details</button></td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={showPrivateColumns ? 6 : 4} className="empty">
                  No contacts match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {filtered.length > visible.length && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn" onClick={() => setShown((n) => n + PAGE_SIZE)}>
            Show more ({(filtered.length - visible.length).toLocaleString()} remaining)
          </button>
        </div>
      )}

      {open && current && (
        <PersonDrawer
          person={current}
          districtName={dName}
          admin={isAdmin(profile)}
          districts={settings.districts}
          affiliationsEnabled={districtAffiliationsEnabled}
          // Judged by the roster record's OWN district, so an affiliation never offers a record that belongs to another district.
          canEditRoster={canEditRosterRecord(current, { isAdmin: isAdmin(profile), canManage: (id) => canManageRoster(profile, id) })}
          onClose={() => setOpen(null)}
          onEditRoster={() => {
            const target = data.find((x) => x.contactId === open.contactId);
            if (target?.contactId) {
              // Start from the roster record itself — its own district and stored Positions — never from the person's combined
              // districts or the Positions they hold elsewhere, so saving unrelated fields cannot move or strip anything.
              const basis = rosterEditBasis(target);
              setEditing({
                id: target.contactId, tenantId: '', districtId: basis.districtId, positions: basis.positions,
                name: target.name, email: target.email, phone: target.phone, notes: target.notes, committees: target.committees,
              } as Contact);
              setOpen(null);
            }
          }}
          onDeleteRoster={() => {
            if (open.contactId && confirm(`Delete the roster record for "${open.name}"? The person stays in Contacts.`)) {
              deleteContact(open.contactId)
                .then(() => setLoginWarning(null))
                .catch((e) => setLoginWarning(`Could not delete the roster record: ${e instanceof Error ? e.message : 'unknown error'}`));
              setOpen(null);
            }
          }}
        />
      )}

      {editing && (
        <ContactForm
          initial={editing === 'new' ? null : editing}
          districts={
            profile.role === 'admin'
              ? settings.districts
              : settings.districts.filter((d) => canManageRoster(profile, d.id))
          }
          settings={settings}
          allowCouncilLevel={isAdmin(profile)}
          canGrantElevated={canGrantElevated}
          usersByEmail={usersByEmail}
          onClose={() => setEditing(null)}
          onSave={async (vals, login) => {
            let contactId: string;
            if (editing === 'new') {
              // Person first: the server names/creates the Person (asking about possible duplicates) and the
              // roster record is created for it. An existing Person is chosen explicitly, never matched by email.
              const entry = await enterPerson(vals);
              if (entry.status === 'cancelled') return; // keep the form open
              if (entry.status === 'exists') {
                throw new Error('That person is already on the roster. Find them in Contacts to edit their record.');
              }
              contactId = entry.contactId;
            } else {
              contactId = editing.id;
              // `moveDistrict` is sent only when the district was deliberately changed in the form.
              await updateContact(editing.id, vals, rosterSaveOptions(editing.districtId, vals.districtId));
            }
            // The roster write above is what the modal's own error display
            // covers (and can safely retry). Close the modal now that it's
            // done, then attempt the login change separately — a failure
            // there (e.g. this email already has a Key 3/admin account a
            // plain chair can't touch) shouldn't reopen the modal and risk
            // creating a duplicate contact on retry, so it surfaces as a
            // page-level warning instead.
            setEditing(null);
            if (login.kind === 'skip' || !vals.email.trim()) return;
            try {
              if (login.kind === 'remove') {
                await removeLogin(vals.email);
              } else {
                await grantLogin({
                  email: vals.email,
                  contactId,
                });
              }
              setLoginWarning(null);
            } catch (e) {
              setLoginWarning(
                `Saved "${vals.name || vals.email}" to the roster, but couldn't ` +
                  `${login.kind === 'remove' ? 'remove their login' : 'grant a login'} ` +
                  `(${e instanceof Error ? e.message : 'unknown error'}). They may already have` +
                  ` a different kind of account — an admin can check Settings → Users.`,
              );
            }
          }}
        />
      )}
      {manualNotice && (
        <ManualEntryNotice
          onCancel={() => setManualNotice(false)}
          onProceed={() => { setManualNotice(false); setEditing('new'); }}
        />
      )}
      {reviewOpen && <ImportMatchReview matches={importMatches.matches} onClose={() => setReviewOpen(false)} onDecided={importMatches.refresh} />}
      {personEntryDialog}
    </div>
  );
}

/** One Person's details. Imported contact information and manually maintained
 *  information are shown side by side and labelled: a manual value wins for
 *  display, the import stays intact underneath and keeps syncing. */
function PersonDrawer({
  person, districtName, admin, districts, affiliationsEnabled, canEditRoster, onClose, onEditRoster, onDeleteRoster,
}: {
  person: DirectoryPerson;
  districtName: (id: string) => string;
  admin: boolean;
  districts: Array<{ id: string; name: string }>;
  affiliationsEnabled: boolean;
  canEditRoster: boolean;
  onClose: () => void;
  onEditRoster: () => void;
  onDeleteRoster: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    preferredEmail: person.manual?.preferredEmail ?? '',
    secondaryEmail: person.manual?.secondaryEmail ?? '',
    phone: person.manual?.phone ?? '',
    notes: person.manual?.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const full = person.visibility === 'full';
  const canEditManual = admin && full && !!person.personId;
  // Anyone who may edit this person's roster record in scope may record ANOTHER address for the same person
  // (the server checks contacts.edit and refuses an address that already belongs to someone else).
  const canManageAlternates = full && !!person.personId && (admin || canEditRoster);
  const [alt, setAlt] = useState('');
  const changeAlternate = async (op: { addAlternateEmail?: string; removeAlternateEmail?: string }) => {
    if (!person.personId) return;
    setBusy(true); setErr(null);
    try {
      await updatePersonContactInfo({ personId: person.personId, ...op });
      setAlt('');
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!person.personId) return;
    setBusy(true);
    setErr(null);
    try {
      await updatePersonContactInfo({
        personId: person.personId,
        preferredEmail: f.preferredEmail.trim() || null,
        secondaryEmail: f.secondaryEmail.trim() || null,
        phone: f.phone.trim() || null,
        notes: f.notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const row = (label: string, value: string | undefined, src?: 'manual' | 'imported', kind?: 'email' | 'phone') => (
    <div style={{ padding: '6px 0' }}>
      <div className="subtitle">{label}</div>
      <div>
        {value ? (kind === 'email' ? <EmailLink email={value} /> : kind === 'phone' ? <PhoneLink phone={value} /> : value) : <span className="subtitle">—</span>}
        {value && src && (
          <span className="subtitle"> · {src === 'manual' ? 'entered manually' : 'imported from my.Scouting'}</span>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      title={person.name}
      subtitle={person.districtIds.length ? person.districtIds.map(districtName).join(' · ') : 'Council level / no district'}
      onClose={onClose}
    >
      {displayedPositions(person).length > 0 && (
        <div style={{ padding: '6px 0' }}>
          <div className="subtitle">Org Chart positions</div>
          <div><PositionList person={person} /></div>
        </div>
      )}
      {!full && (
        <div className="subtitle" style={{ margin: '8px 0' }}>
          Contact details are not available to you for this person.
        </div>
      )}
      {full && !editing && (
        <>
          {row('Email', person.email, person.emailSource, 'email')}
          {person.secondaryEmail && row('Secondary email', person.secondaryEmail, 'manual', 'email')}
          {(person.alternateEmails ?? []).map((e) => (
            <div key={e} style={{ padding: '6px 0' }}>
              <div className="subtitle">Other address</div>
              <div>
                <EmailLink email={e} />
                {canManageAlternates && (
                  <button type="button" className="people-chip-remove" aria-label={`Remove ${e}`} disabled={busy} onClick={() => void changeAlternate({ removeAlternateEmail: e })}>×</button>
                )}
              </div>
            </div>
          ))}
          {row('Phone', person.phone, person.phoneSource, 'phone')}
          {person.notes && row('Notes', person.notes)}
          {admin && person.imported && (person.manual?.preferredEmail || person.manual?.phone) && (
            <div className="subtitle" style={{ marginTop: 8 }}>
              Still on file from imports: {[person.imported.email, person.imported.phone].filter(Boolean).join(' · ') || 'nothing'}. These keep syncing
              underneath your manual entries.
            </div>
          )}
        </>
      )}
      {canEditManual && editing && (
        <div style={{ marginTop: 8 }}>
          <label>Preferred email</label>
          <input type="email" value={f.preferredEmail} placeholder={person.imported?.email ?? ''} onChange={(e) => setF({ ...f, preferredEmail: e.target.value })} />
          <label>Secondary email</label>
          <input type="email" value={f.secondaryEmail} onChange={(e) => setF({ ...f, secondaryEmail: e.target.value })} />
          <label>Phone</label>
          <input value={f.phone} placeholder={person.imported?.phone ?? ''} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <label>Notes</label>
          <textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          <div className="subtitle" style={{ margin: '6px 0' }}>
            Leave a field empty to use the imported value (shown faintly in the box). Manual entries are never overwritten by imports.
          </div>
          {err && <div className="err">{err}</div>}
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn primary" disabled={busy} onClick={() => void save()}>Save</button>
            <button className="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
      {canManageAlternates && !editing && (
        <div style={{ marginTop: 10 }}>
          <div className="subtitle">Known by another email address? Add it to this person instead of creating a duplicate.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <input type="email" placeholder="another@address.org" value={alt} onChange={(e) => setAlt(e.target.value)} />
            <button className="btn" disabled={busy || !alt.trim()} onClick={() => void changeAlternate({ addAlternateEmail: alt.trim() })}>Add address</button>
          </div>
          {err && <div className="err">{err}</div>}
        </div>
      )}
      {canEditManual && !editing && (
        <button className="btn" style={{ marginTop: 10 }} onClick={() => setEditing(true)}>Edit contact information</button>
      )}
      {person.legacyOnly && (
        <div className="subtitle" style={{ marginTop: 10 }}>
          This roster record is not yet linked to a person identity.
        </div>
      )}
      <DistrictAffiliationsSection
        person={person}
        districts={districts}
        districtName={districtName}
        enabled={affiliationsEnabled}
        isAdmin={admin}
        onMutate={(personId, request, requestId) => mutateDistrictAffiliation(personId, request, requestId)}
      />
      {canEditRoster && (
        <div style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <div className="subtitle" style={{ marginBottom: 6 }}>Roster record {person.ref ? `(${person.ref})` : ''}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn" onClick={onEditRoster}>Edit roster record</button>
            <button className="btn danger" onClick={onDeleteRoster}>Delete roster record</button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

export function ContactForm({
  initial,
  districts,
  settings,
  allowCouncilLevel = false,
  canGrantElevated,
  usersByEmail,
  onClose,
  onSave,
}: {
  initial: Contact | null;
  districts: { id: string; name: string }[];
  settings: AppSettings;
  /** Admin only: a Contact may have no district when every Position it holds is council-level. */
  allowCouncilLevel?: boolean;
  /** Key 3 or admin — may grant/remove any non-admin login level, and sees
   *  who already has one. A plain chair only ever gets the capped,
   *  blind-grant checkbox below. */
  canGrantElevated: boolean;
  usersByEmail: Map<string, UserProfile>;
  onClose: () => void;
  onSave: (v: ContactFormValue, login: LoginInstruction) => Promise<void>;
}) {
  const [f, setF] = useState({
    // An existing council-level Contact (no district) stays that way; a district is never filled in for it silently.
    districtId: initial ? (initial.districtId ?? '') : districts[0]?.id ?? '',
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    notes: initial?.notes ?? '',
  });
  const [positions, setPositions] = useState<string[]>(initial?.positions ?? []);
  // What's already on file for this email, if anything — only known for
  // Key 3/admin (see canGrantElevated); a plain chair always starts blind.
  const linkedUser =
    canGrantElevated && initial?.email
      ? (usersByEmail.get(initial.email.trim().toLowerCase()) ?? null)
      : null;
  const [giveLogin, setGiveLogin] = useState(!!linkedUser);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const togglePosition = (name: string) =>
    setPositions((cur) => (cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name]));

  const posList = findPositions(settings.positions, positions);
  const access = deriveAccess(posList, settings.committees);
  const councilLevel = f.districtId === '';
  const districtLevelHeld = positions.filter((n) => (settings.positions.find((p) => p.name === n)?.organizationLevel ?? 'district') !== 'council');
  const councilLevelProblem = !councilLevel ? null
    : !allowCouncilLevel ? 'Only an administrator can manage a Contact without a district.'
    : districtLevelHeld.length ? `A Contact without a district can hold only council-level Positions (not ${districtLevelHeld.join(', ')}).`
    : !initial && positions.length === 0 ? 'Choose at least one council-level Position.'
    : null;

  const loginInstruction = (): LoginInstruction => {
    if (!giveLogin) {
      // A chair has no remove power at all — unchecking never deletes
      // anything for them, only stops a fresh grant. Key 3/admin unchecking
      // an existing login is the explicit "remove it" action.
      return canGrantElevated && linkedUser ? { kind: 'remove' } : { kind: 'skip' };
    }
    return canGrantElevated
      ? {
          kind: 'grant',
          role: access.role,
          districtView: access.districtView,
          keyThree: access.keyThree,
          committees: access.committees,
        }
      : { kind: 'grant', role: 'member', districtView: false, keyThree: false, committees: access.committees };
  };

  return (
    <Modal title={initial ? 'Edit Contact' : 'Add Person Manually'} onClose={onClose}>
      <label>District</label>
      <select value={f.districtId} onChange={(e) => set('districtId', e.target.value)}>
        {(allowCouncilLevel || councilLevel) && <option value="">No district — council level</option>}
        {districts.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>

      {councilLevelProblem && <div className="err">{councilLevelProblem}</div>}
      <label>Position(s) — check every seat they hold</label>
      <div className="drawer-checklist">
        {settings.positions.length === 0 && (
          <div className="subtitle">No positions configured yet.</div>
        )}
        {settings.positions.map((p) => {
          // Council-level Positions are assigned and removed by a council administrator only (the server enforces it too).
          const locked = !allowCouncilLevel && p.organizationLevel === 'council';
          return (
            <label key={p.name} className="checkbox-row" title={locked ? 'Council-level Positions are managed by a council administrator.' : undefined}>
              <input
                type="checkbox"
                checked={positions.includes(p.name)}
                disabled={locked}
                onChange={() => togglePosition(p.name)}
              />
              {p.name}
              {locked && <span className="subtitle"> (council administrator only)</span>}
            </label>
          );
        })}
      </div>
      {positions.length === 0 ? (
        <div className="subtitle" style={{ marginTop: 4 }}>
          None selected — ad-hoc helper, no standing seat.
        </div>
      ) : (
        <div className="subtitle" style={{ marginTop: 4 }}>
          Committee:{' '}
          {access.districtView
            ? 'district-wide'
            : access.committees.length
              ? access.committees.map((id) => recName(settings.committees, id)).join(', ')
              : 'none'}
        </div>
      )}

      <label>Name</label>
      <input value={f.name} onChange={(e) => set('name', e.target.value)} />
      <label>Email</label>
      <input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
      <label>Phone</label>
      <input value={f.phone} onChange={(e) => set('phone', e.target.value)} />
      <label>Notes</label>
      <textarea rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />

      <label className="checkbox-row" style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          checked={giveLogin && !councilLevel}
          disabled={councilLevel}
          onChange={(e) => setGiveLogin(e.target.checked)}
        />
        Give this person a Compass login
      </label>
      {councilLevel && <div className="subtitle" style={{ marginTop: 4 }}>A Contact without a district gets no login here. Logins and Council Access are managed in Settings → Users.</div>}
      <div className="subtitle" style={{ marginTop: 4 }}>
        {canGrantElevated ? (
          giveLogin ? (
            access.keyThree ? (
              'Key 3-level — full control over logins in this district, same as you.'
            ) : access.districtView ? (
              access.role === 'chair' ? 'Edit everything in this district.' : 'View everything in this district.'
            ) : access.role === 'chair' ? (
              `Lead of ${
                access.committees.map((id) => recName(settings.committees, id)).join(', ') || 'no committee'
              }.`
            ) : (
              'Sign in, see this district, and work only tasks assigned to them.'
            )
          ) : linkedUser ? (
            'Unchecking removes their existing login.'
          ) : (
            'No login on file yet.'
          )
        ) : giveLogin ? (
          "They'll be able to sign in, see this district, and update tasks assigned to them. This is always a basic login — a Key 3 leader or admin can grant anything more."
        ) : (
          'Safe to check even if they already have a login — it just refreshes their district/position on file.'
        )}
        {canGrantElevated && ' Never grants admin — only Settings → Users can do that.'}
      </div>

      {err && <div className="err">{err}</div>}

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={
            busy ||
            (!f.districtId && !councilLevel) ||
            !!councilLevelProblem ||
            !f.name.trim() ||
            (giveLogin && !councilLevel && !f.email.trim())
          }
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave(
                {
                  districtId: f.districtId || null,
                  positions,
                  committees: access.committees,
                  name: f.name.trim(),
                  email: f.email.trim(),
                  phone: f.phone.trim() || undefined,
                  notes: f.notes.trim() || undefined,
                },
                councilLevel ? { kind: 'skip' } : loginInstruction(),
              );
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Could not save.');
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </Modal>
  );
}
